import { useCallback, useMemo, useRef, useState } from 'react'

import type {
  Field,
  FieldCreateBody,
  FieldDefinitionPatchBody,
  FieldKind,
} from '@/api/docfiller-types'
import { fieldDisplayName } from '@/api/docfiller-types'
import { cn } from '@/lib/utils'

import {
  anchorPageIndex,
  anchorRectToDocumentRect,
  documentRectToAnchorRect,
  type FieldAnchor,
} from './fieldAnchor'
import {
  documentRectToView,
  viewToDocument,
  type Rect,
} from './viewer/coordinates'
import type { PageModel, Rotation } from './viewer/types'

export type PlacementTool = Extract<FieldKind, 'signature' | 'date'>

export interface FieldPlacementPerPageProps {
  page: PageModel
  scale: number
  rotation: Rotation
  fields: Field[]
  tool: PlacementTool | null
  readOnly: boolean
  onCreateField: (body: FieldCreateBody) => Promise<Field>
  onPatchField: (
    fieldId: string,
    body: FieldDefinitionPatchBody,
  ) => Promise<Field>
  onToolChange: (tool: PlacementTool | null) => void
}

const GRID = 8
const SNAP_DISTANCE = 8
const DEFAULT_SIZE: Record<PlacementTool, { width: number; height: number }> = {
  signature: { width: 180, height: 42 },
  date: { width: 118, height: 30 },
}

type DragMode = 'move' | 'resize'

interface DragState {
  field: Field
  mode: DragMode
  startPoint: { x: number; y: number }
  startRect: Rect
}

export function FieldPlacementPerPage({
  page,
  scale,
  rotation,
  fields,
  tool,
  readOnly,
  onCreateField,
  onPatchField,
  onToolChange,
}: FieldPlacementPerPageProps) {
  const rootRef = useRef<HTMLDivElement | null>(null)
  const [drag, setDrag] = useState<DragState | null>(null)
  const [draftRect, setDraftRect] = useState<Rect | null>(null)
  const [savingFieldId, setSavingFieldId] = useState<string | null>(null)
  const [creating, setCreating] = useState(false)

  const pageRects = useMemo(
    () =>
      fields
        .filter(
          (field) =>
            anchorPageIndex(field.anchor as FieldAnchor | null) === page.index,
        )
        .filter((field) => field.kind === 'signature' || field.kind === 'date')
        .map((field) => ({
          field,
          rect: anchorRectToDocumentRect(
            field.anchor as FieldAnchor | null,
            page.intrinsic,
          ),
        }))
        .filter((row): row is { field: Field; rect: Rect } => row.rect !== null),
    [fields, page.index, page.intrinsic],
  )

  const pointerToDocument = useCallback(
    (event: React.PointerEvent) => {
      const root = rootRef.current
      if (!root) return null
      const bounds = root.getBoundingClientRect()
      const clientX = Number.isFinite(event.clientX) ? event.clientX : 0
      const clientY = Number.isFinite(event.clientY) ? event.clientY : 0
      return viewToDocument(
        { x: clientX - bounds.left, y: clientY - bounds.top },
        page.intrinsic,
        scale,
        rotation,
      )
    },
    [page.intrinsic, rotation, scale],
  )

  const persistRect = useCallback(
    async (field: Field, rect: Rect) => {
      setSavingFieldId(field.uuid)
      try {
        await onPatchField(field.uuid, {
          anchor: {
            page: page.index,
            rect: documentRectToAnchorRect(rect, page.intrinsic),
          },
        })
      } finally {
        setSavingFieldId(null)
      }
    },
    [onPatchField, page.index, page.intrinsic],
  )

  const createAt = useCallback(
    async (point: { x: number; y: number }) => {
      if (!tool || readOnly || creating) return
      const size = DEFAULT_SIZE[tool]
      const rect = snapRect(
        clampRect(
          {
            x: point.x - size.width / 2,
            y: point.y - size.height / 2,
            width: size.width,
            height: size.height,
          },
          page.intrinsic,
        ),
        pageRects.map((row) => row.rect),
        page.intrinsic,
      )
      setCreating(true)
      try {
        await onCreateField({
          kind: tool,
          friendly_name: tool === 'signature' ? 'Signature' : 'Date',
          anchor: {
            page: page.index,
            rect: documentRectToAnchorRect(rect, page.intrinsic),
          },
        })
        onToolChange(null)
      } finally {
        setCreating(false)
      }
    },
    [
      creating,
      onCreateField,
      onToolChange,
      page.index,
      page.intrinsic,
      pageRects,
      readOnly,
      tool,
    ],
  )

  const onRootPointerDown = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      if (!tool || readOnly) return
      if (event.target !== event.currentTarget) return
      const point = pointerToDocument(event)
      if (!point) return
      event.preventDefault()
      void createAt(point)
    },
    [createAt, pointerToDocument, readOnly, tool],
  )

  const startDrag = useCallback(
    (event: React.PointerEvent, field: Field, rect: Rect, mode: DragMode) => {
      if (readOnly) return
      const point = pointerToDocument(event)
      if (!point) return
      event.preventDefault()
      event.stopPropagation()
      event.currentTarget.setPointerCapture?.(event.pointerId)
      setDrag({ field, mode, startPoint: point, startRect: rect })
      setDraftRect(rect)
    },
    [pointerToDocument, readOnly],
  )

  const onDragMove = useCallback(
    (event: React.PointerEvent) => {
      if (!drag) return
      const point = pointerToDocument(event)
      if (!point) return
      event.preventDefault()
      const dx = point.x - drag.startPoint.x
      const dy = point.y - drag.startPoint.y
      const raw =
        drag.mode === 'move'
          ? {
              ...drag.startRect,
              x: drag.startRect.x + dx,
              y: drag.startRect.y + dy,
            }
          : {
              ...drag.startRect,
              width: drag.startRect.width + dx,
              height: drag.startRect.height + dy,
            }
      const otherRects = pageRects
        .filter((row) => row.field.uuid !== drag.field.uuid)
        .map((row) => row.rect)
      setDraftRect(
        snapRect(
          clampRect(raw, page.intrinsic),
          otherRects,
          page.intrinsic,
        ),
      )
    },
    [drag, page.intrinsic, pageRects, pointerToDocument],
  )

  const onDragEnd = useCallback(
    (event: React.PointerEvent) => {
      if (!drag || !draftRect) return
      event.preventDefault()
      event.stopPropagation()
      void persistRect(drag.field, draftRect)
      setDrag(null)
      setDraftRect(null)
    },
    [draftRect, drag, persistRect],
  )

  const placementActive = Boolean(tool) || Boolean(drag)
  const cursorClass = tool ? 'cursor-crosshair' : 'cursor-default'

  return (
    <div
      ref={rootRef}
      className={cn(
        'pointer-events-auto absolute inset-0 z-30 touch-none',
        !placementActive && 'pointer-events-none',
        cursorClass,
      )}
      data-testid="field-placement-layer"
      data-placement-tool={tool ?? 'none'}
      onPointerDown={onRootPointerDown}
      onPointerMove={onDragMove}
      onPointerUp={onDragEnd}
      onPointerCancel={onDragEnd}
    >
      {pageRects.map(({ field, rect }) => {
        const effectiveRect =
          drag?.field.uuid === field.uuid && draftRect ? draftRect : rect
        const viewRect = documentRectToView(
          effectiveRect,
          page.intrinsic,
          scale,
          rotation,
        )
        const saving = savingFieldId === field.uuid
        return (
          <div
            key={field.uuid}
            className={cn(
              'pointer-events-auto absolute rounded-sm border border-primary/70 bg-primary/10 shadow-soft',
              saving && 'border-info bg-info/15',
            )}
            data-testid={`field-placement-box-${field.uuid}`}
            style={{
              left: `${viewRect.x}px`,
              top: `${viewRect.y}px`,
              width: `${Math.max(10, viewRect.width)}px`,
              height: `${Math.max(10, viewRect.height)}px`,
            }}
          >
            <button
              type="button"
              className="absolute inset-0 touch-none cursor-move"
              aria-label={`Move ${fieldDisplayName(field)}`}
              data-testid={`field-placement-move-${field.uuid}`}
              onPointerDown={(event) => startDrag(event, field, rect, 'move')}
            />
            {saving && (
              <span className="pointer-events-none absolute -top-5 left-0 rounded bg-info px-1.5 py-0.5 text-[10px] font-medium text-info-foreground">
                Saving
              </span>
            )}
            <button
              type="button"
              className="absolute -bottom-3 -right-3 size-7 touch-none cursor-nwse-resize rounded-sm border border-primary bg-background shadow-soft md:-bottom-1.5 md:-right-1.5 md:size-4"
              aria-label={`Resize ${fieldDisplayName(field)}`}
              data-testid={`field-placement-resize-${field.uuid}`}
              onPointerDown={(event) => startDrag(event, field, rect, 'resize')}
            />
          </div>
        )
      })}
    </div>
  )
}

function clampRect(rect: Rect, dims: { width: number; height: number }): Rect {
  const width = Math.min(Math.max(rect.width, 28), dims.width)
  const height = Math.min(Math.max(rect.height, 20), dims.height)
  return {
    x: Math.min(Math.max(rect.x, 0), Math.max(0, dims.width - width)),
    y: Math.min(Math.max(rect.y, 0), Math.max(0, dims.height - height)),
    width,
    height,
  }
}

function snapRect(
  rect: Rect,
  siblings: Rect[],
  dims: { width: number; height: number },
): Rect {
  let next = {
    x: snapNumber(rect.x, GRID),
    y: snapNumber(rect.y, GRID),
    width: snapNumber(rect.width, GRID),
    height: snapNumber(rect.height, GRID),
  }
  for (const sibling of siblings) {
    next = snapToSibling(next, sibling)
  }
  return clampRect(next, dims)
}

function snapNumber(value: number, grid: number): number {
  return Math.round(value / grid) * grid
}

function snapToSibling(rect: Rect, sibling: Rect): Rect {
  const next = { ...rect }
  const rectEdges = {
    left: rect.x,
    right: rect.x + rect.width,
    top: rect.y,
    bottom: rect.y + rect.height,
  }
  const siblingEdges = {
    left: sibling.x,
    right: sibling.x + sibling.width,
    top: sibling.y,
    bottom: sibling.y + sibling.height,
  }
  if (Math.abs(rectEdges.left - siblingEdges.left) <= SNAP_DISTANCE) {
    next.x = siblingEdges.left
  } else if (Math.abs(rectEdges.left - siblingEdges.right) <= SNAP_DISTANCE) {
    next.x = siblingEdges.right
  } else if (Math.abs(rectEdges.right - siblingEdges.left) <= SNAP_DISTANCE) {
    next.x = siblingEdges.left - rect.width
  } else if (Math.abs(rectEdges.right - siblingEdges.right) <= SNAP_DISTANCE) {
    next.x = siblingEdges.right - rect.width
  }

  if (Math.abs(rectEdges.top - siblingEdges.top) <= SNAP_DISTANCE) {
    next.y = siblingEdges.top
  } else if (Math.abs(rectEdges.top - siblingEdges.bottom) <= SNAP_DISTANCE) {
    next.y = siblingEdges.bottom
  } else if (Math.abs(rectEdges.bottom - siblingEdges.top) <= SNAP_DISTANCE) {
    next.y = siblingEdges.top - rect.height
  } else if (Math.abs(rectEdges.bottom - siblingEdges.bottom) <= SNAP_DISTANCE) {
    next.y = siblingEdges.bottom - rect.height
  }
  return next
}
